/**
 * Datamodeller på kundens toppnod. Syns bara för global admin.
 */

import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';

interface ReportModelOption {
  code: string;
  nameKey: string;
}

interface Props {
  organizationId: string;
}

export default function OrganizationReportModels({ organizationId }: Props) {
  const { t } = useTranslation();
  const [models, setModels] = useState<ReportModelOption[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      try {
        const [modelsRes, grantsRes] = await Promise.all([
          api.get('/api/reports/generator/models', { params: { organizationId } }),
          api.get(`/api/reports/generator/grants/${organizationId}`),
        ]);
        if (cancelled) return;
        setModels(modelsRes.data?.data?.models ?? []);
        setSelected(grantsRes.data?.data?.modelCodes ?? []);
      } catch (error: unknown) {
        const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
        toast.error(message || t('common.error'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId, t]);

  const toggle = (code: string) => {
    setSelected((current) => (current.includes(code) ? current.filter((item) => item !== code) : [...current, code]));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/api/reports/generator/grants/${organizationId}`, { modelCodes: selected });
      toast.success(t('organizations.reportModels.saved'));
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-textDefault">{t('organizations.reportModels.title')}</h3>
        <p className="mt-1 text-sm text-textTertiary">{t('organizations.reportModels.intro')}</p>
      </div>
      {loading ? (
        <p className="text-sm text-textSecondary">{t('common.loading')}</p>
      ) : models.length === 0 ? (
        <p className="text-sm text-textSecondary">{t('organizations.reportModels.empty')}</p>
      ) : (
        <div className="space-y-2">
          {models.map((model) => (
            <label key={model.code} className="flex items-center gap-2 text-sm text-textDefault">
              <input
                type="checkbox"
                checked={selected.includes(model.code)}
                onChange={() => toggle(model.code)}
              />
              {t(model.nameKey)}
            </label>
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={() => void save()}
        disabled={saving || loading}
        className="px-4 py-2 bg-bgPrimary text-textLight rounded-lg hover:opacity-90 disabled:opacity-50"
      >
        {t('organizations.reportModels.save')}
      </button>
    </div>
  );
}
